import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { signOut } from 'firebase/auth';
import { auth, initFirebaseAuth } from '../services/firebase';

const API_BASE = 'https://tradebot-k4nt.onrender.com/api';

// Create User Context
const UserContext = createContext();

// Custom hook to use the UserContext
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// User Provider Component
const getInitialUser = () => {
  try {
    const storedUser = localStorage.getItem('modanmic_user');
    return storedUser ? JSON.parse(storedUser) : null;
  } catch (err) {
    console.error('Error reading initial user from localStorage:', err);
    localStorage.removeItem('modanmic_user');
    return null;
  }
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(getInitialUser);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const syncIntervalRef = useRef(null);

  // Auto-sync balance every 30 seconds
  const startAutoSync = (username, pin) => {
    // Clear any existing interval first
    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
      syncIntervalRef.current = null;
    }

    // Set up new interval for auto-sync
    syncIntervalRef.current = setInterval(() => {
      refreshUserData(username, pin, true); // Silent refresh
    }, 30000); // 30 seconds
  };

  // Stop auto-sync
  const stopAutoSync = () => {
    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
      syncIntervalRef.current = null;
    }
  };

  // Initialize app state and anonymous Firebase auth
  useEffect(() => {
    const init = async () => {
      await initFirebaseAuth();
      if (user?.telegramName && user?.pin) {
        startAutoSync(user.telegramName, user.pin);
      }
      setLoading(false);
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save user to localStorage whenever it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('modanmic_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('modanmic_user');
    }
  }, [user]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, []);

  // Refresh user data from API (keeps all pages in sync)
  const refreshUserData = async (telegramUsername, pin, silent = false) => {
    if (!silent) setLoading(true);
    
    try {
      const configBody = {
        pin: pin || user?.pin,
        telegrameUsername: telegramUsername || user?.telegramName
      };

      // Must have both username and pin
      if (!configBody.telegrameUsername || !configBody.pin) {
        console.warn('Cannot refresh: missing username or pin');
        if (!silent) setLoading(false);
        return null;
      }

      const config = {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(configBody)
      };

      const url = `${API_BASE}/login`;
      const response = await fetch(url, config);
      
      if (response.ok) {
        const data = await response.json();
        if (data && data.usernameInfo) {
          const userData = {
            ...data.usernameInfo,
            pin: configBody.pin // Keep pin for future refreshes
          };
          setUser(userData);
          console.log('User data refreshed successfully');
          return userData;
        }
      } else {
        console.error('Refresh failed with status:', response.status);
      }
    } catch (err) {
      console.error('Error refreshing user data:', err);
      setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
    return null;
  };

  // Login function
  const login = async (telegramUsername, pin) => {
    setLoading(true);
    setError(null);
    
    try {
      const configBody = {
        pin: pin,
        telegrameUsername: telegramUsername
      };

      const config = {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(configBody)
      };

      const url = `${API_BASE}/login`;
      const response = await fetch(url, config);
      
      if (!response.ok) {
        throw new Error('Login failed');
      }

      const data = await response.json();
      
      if (data && data.usernameInfo) {
        const userData = {
          ...data.usernameInfo,
          pin: pin // Store pin for future refreshes
        };
        setUser(userData);
        // Start auto-sync after login
        startAutoSync(telegramUsername, pin);
        return { success: true, data: userData };
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    // Stop auto-sync immediately
    stopAutoSync();

    // Clear all localStorage keys (including legacy keys)
    localStorage.removeItem('modanmic_user');
    localStorage.removeItem('UserDate');

    // Sign out of Firebase (clears Google auth session)
    signOut(auth).catch((err) => console.error('Firebase signOut error:', err));

    // Clear React state last so components don't re-render before cleanup
    setUser(null);
  };

  // Fetch user from the public GET /api/users/{username} endpoint -- no pin needed.
  // Updates balance (and pin field) in context so all pages stay in sync.
  const fetchUserPublic = useCallback(async (username) => {
    const name = username || user?.telegramName;
    if (!name) return null;
    try {
      const res = await fetch(`${API_BASE}/users/${name}`);
      if (!res.ok) return null;
      const data = await res.json();
      const info = data?.usernameInfo || data;
      if (!info) return null;
      setUser(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          accountBalance: info.accountBalance ?? prev.accountBalance,
          pin: info.pin !== undefined ? info.pin : prev.pin,
          fullName: info.fullName ?? prev.fullName,
          email: info.email ?? prev.email,
          investmentBalance: info.investmentBalance ?? prev.investmentBalance,
          subscriptionPlan: info.subscriptionPlan ?? prev.subscriptionPlan,
          history: info.history ?? prev.history,
          transferHistory: info.transferHistory ?? prev.transferHistory,
        };
      });
      return info;
    } catch (err) {
      console.error('fetchUserPublic error:', err);
      return null;
    }
  }, [user?.telegramName]);

  // Update user balance — stable reference via useCallback; bails out if unchanged
  const updateBalance = useCallback((newBalance) => {
    setUser(prev => {
      if (!prev) return prev;
      if (prev.accountBalance === newBalance) return prev; // no-op: same value, no re-render
      return { ...prev, accountBalance: newBalance };
    });
  }, []); // no deps — uses functional setUser form, safe to omit `user`

  // Add transaction to history
  const addTransaction = (transaction) => {
    if (user) {
      setUser(prev => ({
        ...prev,
        history: [transaction, ...(prev.history || [])],
        lastDepsit: transaction.transactionType === 'deposit' ? transaction.date : prev.lastDepsit
      }));
    }
  };

  // Add transfer to history
  const addTransfer = (transfer) => {
    if (user) {
      setUser(prev => ({
        ...prev,
        transferHistory: [transfer, ...(prev.transferHistory || [])]
      }));
    }
  };

  // Update subscription plan
  const updateSubscription = (planData) => {
    if (user) {
      setUser(prev => ({
        ...prev,
        subscriptionPlan: [...(prev.subscriptionPlan || []), planData],
        dateOfSubscription: new Date().toISOString()
      }));
    }
  };

  // Get formatted balance
  const getFormattedBalance = () => {
    if (!user || !user.accountBalance) return '$0.00';
    const balance = user.accountBalance.replace('$', '').replace(/,/g, '');
    return `$${parseFloat(balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Get numeric balance
  const getNumericBalance = () => {
    if (!user || !user.accountBalance) return 0;
    return parseFloat(user.accountBalance.replace('$', '').replace(/,/g, ''));
  };

  // Set user data directly (used after external auth flows)
  const setUserFromData = (newUser) => {
    if (!newUser) return;
    setUser(newUser);
    // Start auto-sync when we have credentials
    if (newUser?.telegramName && newUser?.pin) {
      startAutoSync(newUser.telegramName, newUser.pin);
    }
  };

  // Context value
  const value = {
    user,
    loading,
    error,
    login,
    logout,
    refreshUserData,
    fetchUserPublic,
    updateBalance,
    addTransaction,
    addTransfer,
    updateSubscription,
    getFormattedBalance,
    getNumericBalance,
    setUserFromData,
    // Expose user properties for easy access
    username: user?.telegramName,
    email: user?.email,
    fullName: user?.fullName,
    accountNumber: user?.accountNumber,
    balance: user?.accountBalance,
    history: user?.history || [],
    transferHistory: user?.transferHistory || [],
    walletHistory: user?.walletHistory || [],
    subscriptionPlan: user?.subscriptionPlan || [],
    wallet: user?.wallet || {},
    investmentBalance: user?.investmentBalance || '0',
    refralBalance: user?.refralBalance || 0,
    refralList: user?.refralList || [],
    stripeAccountType: user?.stripeAccountType || {},
    stripCardType: user?.stripCardType || {},
    dateOfRegistration: user?.dateOfRegistration,
    lastDepsit: user?.lastDepsit,
    validated: user?.validated
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
